import {Component, Emitter, Material, MeshComponent, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {WLUtils} from '../utils/WLUtils.js';
import {FlatOpaque} from '../types/materials.js';

enum TransitionType {
    IrisToBlack,
    IrisFromBlack,
    FadeToBlack,
    FadeFromBlack,
    None,
}

interface IrisMaterial extends Material {
    color: number[];
    irisSize: number;
}

export class TransitionController extends Component {
    static TypeName = 'transition-controller';

    @property.object({required: true})
    irisObject!: Object3D;

    @property.object({required: true})
    fadeObject!: Object3D;

    @property.float(1.0)
    irisTransitionTime = 1.0;

    @property.float(0.1)
    fadeTransitionTime = 0.1;

    @property.color(0.0, 0.0, 0.0, 1.0)
    irisColor = [0.0, 0.0, 0.0, 1.0];

    @property.color(1.0, 1.0, 1.0, 1.0)
    fadeColor = [1.0, 1.0, 1.0, 1.0];

    get transitionInProgress(): boolean {
        return this._transitionInProgress !== TransitionType.None;
    }

    private _transitionInProgress = TransitionType.None;
    private _irisSize = 0;
    private _irisMaterial: IrisMaterial;
    private _fadeMaterial: FlatOpaque;
    private _alphaValue = 1.0;

    init(): void {
        // enable the object on init so we start black
        WLUtils.setActive(this.object, true);
        this.object.resetPosition();
    }

    start() {
        this._irisMaterial = this.irisObject.getComponent(MeshComponent)
            .material as IrisMaterial;

        this._fadeMaterial = this.fadeObject.getComponent(MeshComponent)
            .material as FlatOpaque;
        this.black(); // Set the initial state to black
    }

    private _resolve: (value: void | PromiseLike<void>) => void;

    black(): void {
        this._transitionInProgress = TransitionType.None;
        this._setObjectsActive();
        this._irisSize = 0;
        this._irisMaterial.irisSize = 0;
        this._fadeMaterial.setColor([
            this.fadeColor[0],
            this.fadeColor[1],
            this.fadeColor[2],
            this._alphaValue,
        ]);
    }

    irisToBlack(): Promise<void> {
        this._transitionInProgress = TransitionType.IrisToBlack;
        this._setObjectsActive();

        this._irisSize = 0.5;
        this._irisMaterial.color = this.irisColor;

        const completionPromise = new Promise<void>((r) => (this._resolve = r));
        return completionPromise;
    }

    irisFromBlack(): Promise<void> {
        this._transitionInProgress = TransitionType.IrisFromBlack;
        this._setObjectsActive();
        this._irisSize = 0;
        const completionPromise = new Promise<void>((r) => (this._resolve = r));
        return completionPromise;
    }

    fadeToBlack(): Promise<void> {
        this._transitionInProgress = TransitionType.FadeToBlack;
        this._setObjectsActive();
        this._irisSize = 0; // fade does not use Iris.
        this._alphaValue = 0;
        this._fadeMaterial.setColor(this.fadeColor);
        const completionPromise = new Promise<void>((r) => (this._resolve = r));
        return completionPromise;
    }

    fadeFromBlack(): Promise<void> {
        this._transitionInProgress = TransitionType.FadeFromBlack;
        this._setObjectsActive();
        this._alphaValue = 1;
        this._fadeMaterial.setColor(this.fadeColor);
        const completionPromise = new Promise<void>((r) => (this._resolve = r));
        return completionPromise;
    }

    private _setObjectsActive() {
        WLUtils.setActive(this.object, true);
        if (
            this._transitionInProgress === TransitionType.IrisToBlack ||
            this._transitionInProgress === TransitionType.IrisFromBlack
        ) {
            WLUtils.setActive(this.irisObject, true);
            WLUtils.setActive(this.fadeObject, false);
        } else if (
            this._transitionInProgress === TransitionType.FadeToBlack ||
            this._transitionInProgress === TransitionType.FadeFromBlack
        ) {
            WLUtils.setActive(this.fadeObject, true);
            WLUtils.setActive(this.irisObject, false);
        }
    }

    update(dt: number) {
        if (this._transitionInProgress === TransitionType.None) {
            return;
        }

        if (this._transitionInProgress === TransitionType.IrisToBlack) {
            this._irisSize -= dt * this.irisTransitionTime;
            if (this._irisSize <= 0) {
                this._irisSize = 0;
                this._irisMaterial.irisSize = this._irisSize;
                this._transitionInProgress = TransitionType.None;
                this._resolve();
            }
        } else if (this._transitionInProgress === TransitionType.IrisFromBlack) {
            this._irisSize += dt * this.irisTransitionTime;
            if (this._irisSize >= 1) {
                this._irisSize = 1;
                this._irisMaterial.irisSize = this._irisSize;
                WLUtils.setActive(this.object, false);
                this._transitionInProgress = TransitionType.None;
                this._resolve();
            }
        } else if (this._transitionInProgress === TransitionType.FadeToBlack) {
            this._alphaValue += dt / this.fadeTransitionTime;
            if (this._alphaValue >= 1) {
                this._alphaValue = 1;
                this._transitionInProgress = TransitionType.None;
                this._resolve();
            }
        } else if (this._transitionInProgress === TransitionType.FadeFromBlack) {
            this._alphaValue -= dt / this.fadeTransitionTime;
            if (this._alphaValue <= 0) {
                this._alphaValue = 0;
                WLUtils.setActive(this.object, false);
                this._transitionInProgress = TransitionType.None;
                this._resolve();
            }
        }

        if (
            this._transitionInProgress === TransitionType.IrisToBlack ||
            this._transitionInProgress === TransitionType.IrisFromBlack
        ) {
            this._irisMaterial.irisSize = this._irisSize;
        } else if (
            this._transitionInProgress === TransitionType.FadeFromBlack ||
            this._transitionInProgress === TransitionType.FadeToBlack
        ) {
            this._fadeMaterial.setColor([
                this.fadeColor[0],
                this.fadeColor[1],
                this.fadeColor[2],
                this._alphaValue,
            ]);
        }
    }
}
